/**
 * SimpleMarkdown Component
 * Renders basic markdown (bold, italic, code, headings, lists) as React elements.
 * Safe — all content goes through JSX escaping, no dangerouslySetInnerHTML.
 */

import React from 'react';

export const SimpleMarkdown: React.FC<{ content: string }> = ({ content }) => {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];
  let listType: 'ul' | 'ol' | null = null;

  const flushList = () => {
    if (listItems.length > 0 && listType) {
      const Tag = listType;
      elements.push(
        <Tag key={`list-${elements.length}`} className={listType === 'ul' ? 'list-disc pl-5 my-1' : 'list-decimal pl-5 my-1'}>
          {listItems}
        </Tag>
      );
      listItems = [];
      listType = null;
    }
  };

  const formatInline = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      const codeMatch = remaining.match(/`(.+?)`/);
      const italicMatch = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/);

      const matches = [
        boldMatch ? { type: 'bold', match: boldMatch, idx: boldMatch.index! } : null,
        codeMatch ? { type: 'code', match: codeMatch, idx: codeMatch.index! } : null,
        italicMatch ? { type: 'italic', match: italicMatch, idx: italicMatch.index! } : null,
      ].filter(Boolean).sort((a, b) => a!.idx - b!.idx);

      if (matches.length === 0) {
        parts.push(remaining);
        break;
      }

      const first = matches[0]!;
      if (first.idx > 0) parts.push(remaining.slice(0, first.idx));

      if (first.type === 'bold') {
        parts.push(<strong key={key++}>{first.match![1]}</strong>);
        remaining = remaining.slice(first.idx + first.match![0].length);
      } else if (first.type === 'code') {
        parts.push(<code key={key++} className="px-1 py-0.5 bg-gray-200 rounded text-xs">{first.match![1]}</code>);
        remaining = remaining.slice(first.idx + first.match![0].length);
      } else {
        parts.push(<em key={key++}>{first.match![1]}</em>);
        remaining = remaining.slice(first.idx + first.match![0].length);
      }
    }

    return parts.length === 1 ? parts[0] : <>{parts}</>;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    const headingMatch = line.match(/^(#{1,3})\s+(.+)/);
    if (headingMatch) {
      flushList();
      const level = headingMatch[1].length;
      const text = formatInline(headingMatch[2]);
      if (level === 1) elements.push(<h3 key={i} className="text-base font-bold mt-3 mb-1">{text}</h3>);
      else if (level === 2) elements.push(<h4 key={i} className="text-sm font-bold mt-2 mb-1">{text}</h4>);
      else elements.push(<h5 key={i} className="text-sm font-semibold mt-2 mb-0.5">{text}</h5>);
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(<li key={i}>{formatInline(line.replace(/^[-*]\s+/, ''))}</li>);
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(<li key={i}>{formatInline(line.replace(/^\d+\.\s+/, ''))}</li>);
      continue;
    }

    if (line.trim() === '') {
      flushList();
      continue;
    }

    flushList();
    elements.push(<p key={i} className="my-0.5">{formatInline(line)}</p>);
  }

  flushList();
  return <div className="space-y-0.5">{elements}</div>;
};
